import { BaseKey } from '@refinedev/core';
import { message } from 'antd';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { Dayjs } from 'dayjs';

import { PACKAGE_URLS } from '@/constants/urls';
import FileData from '@/model/General/FileData';
import { PackageMessage } from '@/model/Logistic/PackageMessage';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { ShippingLabel } from '@/model/Logistic/ShippingLabel/ShippingLabel';
import { RESOURCES } from '@/Resources';

class ReceptionApi {
  axiosInstance: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  createAddress = async (addressData: ShippingLabel) => {
    try {
      const response = await this.axiosInstance.post('/shipping_labels', addressData);

      return response.data;
    } catch (error) {
      console.error('Error creating address:', error);
      throw error;
    }
  };

  async printShippingLabel(receptionId: number) {
    try {
      const config: AxiosRequestConfig = {
        responseType: 'blob',
        params: {
          id: receptionId,
        },
      };
      const response = await this.axiosInstance.get(
        `${RESOURCES.RECEPTIONS}/${receptionId}/shipping_label`,
        config,
      );
      const { headers, data } = response;
      const fileName = headers['content-disposition'].split('filename=')[1];
      const type = headers['content-type'];
      const url = window.URL.createObjectURL(new Blob([data], { type: type }));
      const a = document.createElement('a'); // Creamos un botón para simular el click de descarga

      a.href = url;
      a.setAttribute('download', fileName);
      a.click();

      // Se limpia la memoria
      setTimeout(function () {
        window.URL.revokeObjectURL(url);
      }, 1000);

      return response.data;
    } catch (error) {
      console.log(error);
    }
  }

  uploadFiles(files: FileData[], packageId: BaseKey): Promise<any[]> {
    if (!files || files.length === 0) {
      return Promise.resolve([]);
    } else {
      const uploadPromises: Promise<any>[] = [];

      files.forEach(file => {
        if (!file.blobFile) {
          return;
        }

        const formData = new FormData();

        formData.append('photo', file.blobFile);
        formData.append('package', String(packageId));

        uploadPromises.push(
          this.axiosInstance
            .post(`${PACKAGE_URLS.PHOTOS}/`, formData)
            .then(response => {
              return response.data;
            })
            .catch(error => {
              message.error(error.response?.data?.message || 'Error uploading file');

              console.error(`Error uploading file ${file.title}:`, error);
              throw error;
            }),
        );
      });

      return Promise.all(uploadPromises);
    }
  }

  removeFiles(files: FileData[]): Promise<any[]> {
    if (!files || files.length === 0) {
      return Promise.resolve([]);
    } else {
      const removePromises: Promise<any>[] = [];

      files.forEach(file => {
        removePromises.push(
          this.axiosInstance
            .delete(`${PACKAGE_URLS.PHOTOS}/${file.id}`)
            .then(response => {
              return response.data;
            })
            .catch(error => {
              console.error(`Error removing file ${file.title}:`, error);
              throw error;
            }),
        );
      });

      return Promise.all(removePromises);
    }
  }

  async updateReceptionState(receptionId: number, newState: PACKAGE_STATES, date?: Dayjs) {
    try {
      const response = await this.axiosInstance.patch(`/receptions/${receptionId}`, {
        status: newState,
        date: date ? date : undefined,
        active_claim: false,
      });

      return response.data;
    } catch (error) {
      console.error('Error updating reception state:', error);
      throw error;
    }
  }

  async updateReceptionShipper(receptionId: number, shipperId: number) {
    try {
      const response = await this.axiosInstance.patch(`/receptions/${receptionId}`, {
        shipper: shipperId,
        active_claim: false,
      });

      return response.data;
    } catch (error) {
      console.error('Error updating reception shipper:', error);
      throw error;
    }
  }
  async updateReceptionTechnician(receptionId: number, technicianId?: number) {
    try {
      const response = await this.axiosInstance.patch(`/receptions/${receptionId}`, {
        technician: technicianId ? technicianId : null,
        active_claim: false,
      });

      return response.data;
    } catch (error) {
      console.error('Error updating reception technician:', error);
      throw error;
    }
  }

  async updateReception(receptionId: number, receptionData: Partial<Reception>) {
    try {
      const response = await this.axiosInstance.patch(`/receptions/${receptionId}`, {
        ...receptionData,
        active_claim: false,
      });

      return response.data;
    } catch (error) {
      console.error('Error updating reception tracking number:', error);
      throw error;
    }
  }

  async getMessages(receptionId: number) {
    try {
      const response = await this.axiosInstance.get(`/messages`, {
        params: { package: receptionId },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching shipment messages:', error);
      throw error;
    }
  }

  async postMessage(receptionId: number, message: PackageMessage) {
    try {
      const response = await this.axiosInstance.post(`/messages`, {
        ...message,
        destination_users: message.destination_users_data,
      });

      return response.data;
    } catch (error) {
      console.error('Error posting shipment message:', error);
      throw error;
    }
  }

  async sendClaimEmail(receptionId: number, emails: string) {
    try {
      const response = await this.axiosInstance.get(`/send_claim_email`, {
        params: {
          email: emails,
          tipo: 1,
          paquete_id: receptionId,
        },
      });

      return response;
    } catch (error) {
      console.error('Error sending claim email:', error);
    }
  }
}

export default ReceptionApi;
